/**
 * DIKDOC Design System v7.3 — Couleurs par domaine.
 *
 * Convention :
 *   Clinical    #0EA5E9 — Sky blue (patients, soins, labo)
 *   Security    #EF4444 — Red (SOC, alerts, audit)
 *   Operations  #10B981 — Emerald (logistique, pharma, imagerie)
 *   AI          #8B5CF6 — Violet (icd10, drugs, predictive)
 *   Governance  #F59E0B — Amber (compliance, SIEM admin, IAM)
 *
 * Règle : un user sait INSTANTANÉMENT dans quel domaine il est
 * grâce à la couleur dominante + icône + label.
 */
export type Domain = "clinical" | "security" | "operations" | "ai" | "governance" | "neutral";

export const DOMAINS: Record<Domain, {
  label: string;
  short: string;
  color: string;       // hex
  textClass: string;   // text-xxx-400
  bgClass: string;     // bg-xxx-500/10
  borderClass: string; // border-xxx-500/30
  badge: string;
  purpose: string;
  verb: string;        // "view" vs "configure"
}> = {
  clinical: {
    label: "Clinical Operations Center",
    short: "Clinical",
    color: "#0EA5E9",
    textClass: "text-sky-400",
    bgClass: "bg-sky-500/10",
    borderClass: "border-sky-500/30",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    purpose: "Patients · Soins · Labo · Imagerie · Pharmacie · Facturation",
    verb: "traiter",
  },
  security: {
    label: "Security Operations Center",
    short: "SOC",
    color: "#EF4444",
    textClass: "text-red-400",
    bgClass: "bg-red-500/10",
    borderClass: "border-red-500/30",
    badge: "bg-red-500/20 text-red-300 border-red-500/40",
    purpose: "Threats · Alertes · Audit · IoMT live",
    verb: "observer",
  },
  operations: {
    label: "Operations Center",
    short: "Ops",
    color: "#10B981",
    textClass: "text-emerald-400",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/30",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    purpose: "Stocks · Dispensation · Traçabilité · Stocks",
    verb: "coordonner",
  },
  ai: {
    label: "AI Center",
    short: "AI",
    color: "#8B5CF6",
    textClass: "text-violet-400",
    bgClass: "bg-violet-500/10",
    borderClass: "border-violet-500/30",
    badge: "bg-violet-500/20 text-violet-300 border-violet-500/40",
    purpose: "ICD-10 · Drug interactions · Notes · Predictions",
    verb: "augmenter",
  },
  governance: {
    label: "Platform Governance Center",
    short: "Governance",
    color: "#F59E0B",
    textClass: "text-amber-400",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/30",
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    purpose: "IAM · Compliance · Policies · Vault · Infrastructure",
    verb: "administrer",
  },
  neutral: {
    label: "Mission Control",
    short: "Mission",
    color: "#94a3b8",
    textClass: "text-slate-400",
    bgClass: "bg-slate-500/10",
    borderClass: "border-slate-500/30",
    badge: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    purpose: "Vue exécutive · Tous domaines",
    verb: "piloter",
  },
};

/** Trouve le domaine par préfixe de route. */
export function domainFromPath(path: string): Domain {
  if (path.startsWith("/soc") || path.startsWith("/admin/siem/notifications")) return "security";
  if (path.startsWith("/admin/administrateur") || path.startsWith("/cockpits") || path === "/") return "clinical";
  if (path.startsWith("/ai") || path.startsWith("/consultation")) return "ai";
  if (path.startsWith("/admin/siem") || path.startsWith("/admin") || path.startsWith("/governance")) return "governance";
  if (path.startsWith("/pharmacy") || path.startsWith("/pharma")) return "operations";
  return "neutral";
}